import { useTranslation } from "react-i18next";
import type { AutoSplitPreviewProps } from "./types";

// Long enough to see the shape of it; the rest is a count, not a list to read.
const SHOWN_GROUPS = 6;

const CHOICE_CLASS = "flex-1 rounded-md border px-2 py-1.5 text-[13px] font-medium";

export const AutoSplitPreview = ({ plan, switchChoice, onSwitchChoice, onTake }: AutoSplitPreviewProps) => {
  const { t } = useTranslation();
  const { groups } = plan;
  const hidden = groups.length - SHOWN_GROUPS;
  const asksAboutSwitches = plan.availableSwitchCount > 0;
  const choiceClass = (choice: Exclude<typeof switchChoice, "unanswered">) =>
    `${CHOICE_CLASS} ${
      switchChoice === choice
        ? "border-blue-600 bg-blue-600 text-white"
        : "border-slate-300 bg-white text-slate-800 hover:border-blue-500 hover:bg-blue-50"
    }`;

  return (
    <div data-testid="auto-split-preview" className="flex w-full flex-col gap-1.5">
      <p className="text-[13px] leading-snug text-slate-600">
        {t(
          plan.method === "rooms"
            ? "smartSurveyor.cablesBeta.circuitAutoSplitByRooms"
            : "smartSurveyor.cablesBeta.circuitAutoSplitByRows",
        )}
      </p>
      {asksAboutSwitches && (
        // Taking a switch off another circuit changes that circuit too, so it is his call, not the split's.
        <div data-testid="auto-split-switch-question" className="flex flex-col gap-1.5 rounded-md bg-white/80 p-2">
          <p className="text-[13px] font-medium leading-snug text-slate-800">
            {t("smartSurveyor.cablesBeta.circuitAutoSplitSwitchQuestion", { count: plan.availableSwitchCount })}
          </p>
          <div className="flex gap-1.5">
            <button
              type="button"
              aria-pressed={switchChoice === "take"}
              onClick={() => onSwitchChoice("take")}
              className={choiceClass("take")}
            >
              {t("smartSurveyor.cablesBeta.circuitAutoSplitSwitchTake")}
            </button>
            <button
              type="button"
              aria-pressed={switchChoice === "leave"}
              onClick={() => onSwitchChoice("leave")}
              className={choiceClass("leave")}
            >
              {t("smartSurveyor.cablesBeta.circuitAutoSplitSwitchLeave")}
            </button>
          </div>
        </div>
      )}
      {asksAboutSwitches && switchChoice === "unanswered" ? null : groups.length < 2 && plan.borrowedSwitchCount === 0 ? (
        <p className="text-[13px] font-medium text-slate-700">
          {t("smartSurveyor.cablesBeta.circuitAutoSplitFits")}
        </p>
      ) : (
        <>
          <ol className="flex flex-col gap-0.5">
            {groups.slice(0, SHOWN_GROUPS).map((group, index) => (
              <li
                key={group.deviceIds[0]}
                data-testid="auto-split-group"
                className="flex items-baseline gap-2 rounded bg-white/80 px-1.5 py-1 text-[13px]"
              >
                <span className="w-5 flex-none text-right font-semibold tabular-nums text-slate-500">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-slate-800">
                  {group.roomNames.length
                    ? group.roomNames.join(", ")
                    : t("smartSurveyor.cablesBeta.circuitAutoSplitRowGroup", { index: index + 1 })}
                </span>
                <span className="flex-none tabular-nums text-slate-600">
                  {t("smartSurveyor.cablesBeta.circuitAutoSplitDevices", {
                    count: group.deviceIds.length,
                  })}
                </span>
                <span className="w-16 flex-none text-right text-[12px] text-slate-500">
                  {t(
                    index === 0
                      ? "smartSurveyor.cablesBeta.circuitAutoSplitStays"
                      : "smartSurveyor.cablesBeta.circuitAutoSplitNew",
                  )}
                </span>
              </li>
            ))}
          </ol>
          {hidden > 0 && (
            <p className="pl-7 text-[12.5px] text-slate-500">
              {t("smartSurveyor.cablesBeta.circuitAutoSplitMore", { count: hidden })}
            </p>
          )}
          <button
            type="button"
            onClick={onTake}
            className="self-end rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90"
          >
            {groups.length < 2
              ? t("smartSurveyor.cablesBeta.circuitAutoSplitTakeSwitches", { count: plan.borrowedSwitchCount })
              : t("smartSurveyor.cablesBeta.circuitAutoSplitGo", { count: groups.length })}
          </button>
        </>
      )}
    </div>
  );
};
